import i18next from "i18next";
import { toast } from "sonner";

import type {
  ErrorResponse,
  FetchResponse,
  FetchResponseOfError,
} from "Infrastructure/Api/Api";

type BaseResponse<TData = unknown> =
  | FetchResponse<TData, number>
  | ErrorResponse;

type SuccessResponse<TResponse extends BaseResponse> = Extract<
  TResponse,
  { status: 200 | 201 | 202 | 204 }
>;
export type Statuses400s = 400 | 401 | 403 | 404 | 405 | 409 | 422;

type SuccessResponseData<TResponse extends BaseResponse> =
  SuccessResponse<TResponse>["data"];

export const isSuccessResponse = <T extends { status: number }>(
  response: T,
): response is T & { status: 200 | 201 | 202 | 204 } =>
  response.status >= 200 && response.status < 300;

export const isErrorStatus = <T extends { status: number }>(
  response: T,
): response is T & { status: Statuses400s } =>
  response.status >= 400 && response.status < 500;

export class ApiCallError<TData = unknown> extends Error {
  data: TData;

  constructor(data: TData) {
    super();
    this.data = data;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiCallError<FetchResponseOfError> {
    return obj instanceof ApiCallError || obj?.isApiException === true;
  }
}

/** Extrahuje error-case varianty z FetchResponse union (odstraní success varianty) */
export type ApiErrorDataOf<TFetchResponse> = Exclude<
  TFetchResponse,
  { status: 200 | 201 | 202 | 204 }
>;

export type ApiErrorConfig = {
  /** i18next key prefix pro error překlady, např. "ApiErrors:SaveClient".
   *  Získat přes: getResourcePath(Resources.ApiErrors.SaveClient) */
  translationPrefix?: string;
  /** Pokud true, žádný toast se nezobrazí — error se stále hodí */
  preventErrorToast?: boolean;
};

export const isFetchResponseOfError = (obj: any): obj is FetchResponseOfError =>
  !!obj.data && typeof obj.status === "number";

export const handleResponse = (
  response?: { data: unknown; status: number } | Error | null,
) => {
  if (!response) {
    return;
  }

  processResponseStatusCode(response as any);
};

export const handleResponseError = (error: any) => {
  const errorMessage = getMessageFromErrorResponse(error);
  const status = error.data.status ?? 500;
  if (errorMessage && status >= 500) {
    toast.error(errorMessage);
  }
};

function getMessageFromErrorResponse(response: any) {
  let errorMessage: string | undefined;
  let responseError = response;
  if (ApiCallError.isApiException(responseError)) {
    responseError = responseError.data;
  }

  if (isFetchResponseOfError(responseError)) {
    responseError = responseError.data;
  }

  //if has property title
  if (responseError?.title?.length > 0) {
    return responseError.title;
  }

  if (responseError instanceof Error) {
    errorMessage = responseError.message;
  }

  return errorMessage ?? "Unknown error";
}

/**
 *  Process response and throw error if status code is not 2xx
 * @param request request function
 * @param errorConfig Optional error config — when provided, shows a translated toast on 4xx errors
 * @returns Processed response
 */
export const processResponse =
  <TArgs, TResponse extends BaseResponse>(
    request: (args: TArgs, headers?: Headers) => Promise<TResponse>,
    errorConfig?: ApiErrorConfig,
  ) =>
  async (args: TArgs): Promise<SuccessResponseData<TResponse>> => {
    const response = await request(args);
    return processResponseStatusCode(response, errorConfig);
  };

/**
 * Process response and throw error if status code is not 2xx
 * @param request request function
 * @returns Processed response
 */
export const processResponseWithData =
  <T>(
    request: () => Promise<FetchResponse<{ Data: T }, 200> | ErrorResponse>,
  ) =>
  async () => {
    const response = await request();
    return processResponseStatusCode(response)?.Data;
  };

function processResponseStatusCode<TResponse extends BaseResponse>(
  response: TResponse,
  config?: ApiErrorConfig,
): SuccessResponseData<TResponse> {
  if (response?.status >= 400 || response?.status === 0) {
    if (!config?.preventErrorToast) {
      showApiErrorToast(response, config?.translationPrefix);
    }
    throw new ApiCallError(response);
  }

  return response?.data;
}

function showApiErrorToast(response: any, translationPrefix?: string): void {
  // 4xx s typovaným ErrorStatus (jen pokud máme prefix)
  if (translationPrefix && response?.status >= 400 && response?.status < 500) {
    const errors = response?.data?.errors as
      | Record<string, Array<{ message?: string | null }>>
      | null
      | undefined;

    if (errors) {
      for (const details of Object.values(errors)) {
        for (const detail of details) {
          if (detail.message) {
            const specificKey = `${translationPrefix}.${detail.message}`;
            if (i18next.exists(specificKey)) {
              toast.error(i18next.t(specificKey) as string);
              return;
            }
          }
        }
      }
    }
    // Žádný specifický překlad — General fallback
    const generalKey = `${translationPrefix}.General`;
    const fallbackKey = "ApiErrors:General";
    toast.error(
      i18next.t(
        i18next.exists(generalKey) ? generalKey : fallbackKey,
      ) as string,
    );
    return;
  }
  // 5xx / network / bez prefixu: bez toastu (handleResponseError stále k dispozici pro 5xx)
}
